import { useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { RefObject } from 'react';
import { sdkQueryClient } from '../queryClient';
import type { Survey, SurveySession } from '../fetchSurvey/types/survey';
import type { SurveyAnswers, SubmitRequestBody } from '../submitSurvey/types';
import {
  resolveSubmitInput,
  buildSubmitRequestBody,
  buildExternalSubmitUrl,
  submitSurveyResponse,
} from '../submitSurvey/utils';
import { sdkLogger } from '../logger/logger';
import { toSdkLoggableError } from '../types/shared';

type UsePartialSaveProps = {
  session?: SurveySession | null;
  survey?: Survey | null;
  answers: SurveyAnswers;
  enabled: boolean;
  debounceMs: number;
  hasUserInteractedRef: RefObject<boolean>;
  isFinalSubmitting: boolean;
};

/**
 * Debounced partial save hook — POSTs current answers with surveySubmitted=false.
 */
export function usePartialSave({
  session,
  survey,
  answers,
  enabled,
  debounceMs,
  hasUserInteractedRef,
  isFinalSubmitting,
}: UsePartialSaveProps) {
  const lastSavedSnapshotRef = useRef<string>('');
  const isFinalSubmittingRef = useRef(isFinalSubmitting);

  useEffect(() => {
    isFinalSubmittingRef.current = isFinalSubmitting;
  }, [isFinalSubmitting]);

  const mutation = useMutation(
    {
      mutationFn: ({ url, requestBody }: { url: string; requestBody: SubmitRequestBody }) =>
        submitSurveyResponse({ url, requestBody }),
    },
    sdkQueryClient
  );

  const mutateAsyncRef = useRef(mutation.mutateAsync);
  mutateAsyncRef.current = mutation.mutateAsync;

  useEffect(() => {
    if (!enabled || !session?.instanceId || !survey || !hasUserInteractedRef.current) {
      return;
    }

    if (isFinalSubmittingRef.current) {
      return;
    }

    const snapshot = JSON.stringify(answers);
    if (snapshot === lastSavedSnapshotRef.current) {
      return;
    }

    const timer = setTimeout(() => {
      if (isFinalSubmittingRef.current) return;

      try {
        const resolved = resolveSubmitInput(answers, session, survey);
        const requestBody = buildSubmitRequestBody(resolved.answers, resolved.questions ?? []);
        const url = buildExternalSubmitUrl(resolved.instanceId, resolved.surveyLanguage ?? 'en', false);

        void mutateAsyncRef
          .current({ url, requestBody })
          .then(() => {
            lastSavedSnapshotRef.current = snapshot;
            sdkLogger.info('Partial survey response saved');
          })
          .catch(err => {
            sdkLogger.warn('Partial survey response save failed', { err: toSdkLoggableError(err) });
          });
      } catch (err) {
        sdkLogger.warn('Partial survey response save skipped', { err: toSdkLoggableError(err) });
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [answers, debounceMs, enabled, hasUserInteractedRef, session, survey]);

  return {
    data: mutation.data ?? null,
    error: mutation.error ?? null,
    isLoading: mutation.isPending,
  };
}
